// 詳細パネル v2 の「制作期限」ブロック（発送済以降）。design/handoff-README.md §3 ④-3。
// 発送 → 期限（日数を1件ずつ上書き）・残り日数・リマインド回数と履歴。
import { useEffect, useState } from "react";
import { updateGfDeal } from "../../../../api/giftingClient";
import { addDaysYmd, deadlineInfo, fmtMD, fmtYmd } from "../../../../lib/giftingRules";
import type { GfActionKey, GfContactLog, GfDealDetail, GfEntry } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";

export function DeadlineBlock({
  entry,
  detail,
  nowIso,
  actionKey,
  changed,
  toast,
}: {
  entry: GfEntry;
  detail: GfDealDetail | null;
  nowIso: string;
  actionKey: GfActionKey;
  changed: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const dl = deadlineInfo(entry, nowIso);
  const shipYmd = entry.shipped_at ? fmtYmd(entry.shipped_at) : "";
  const [days, setDays] = useState(dl?.days != null ? String(dl.days) : "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDays(dl?.days != null ? String(dl.days) : "");
  }, [dl?.days]);

  if (!dl) return null;

  const reminders: GfContactLog[] = (detail?.contact_log ?? []).filter((l) => l.kind === "reminder" && l.direction === "out");

  const commit = async () => {
    const n = Math.round(Number(days));
    if (!shipYmd || !Number.isFinite(n) || n < 1 || n > 365) {
      toast("日数は1〜365で入力してください");
      setDays(dl.days != null ? String(dl.days) : "");
      return;
    }
    if (n === dl.days) return;
    setBusy(true);
    try {
      await updateGfDeal(entry.id, { post_deadline: addDaysYmd(shipYmd, n) });
      await changed();
      toast(`制作期限を発送から${n}日に変更しました`);
    } catch (e) {
      toast(errMsg(e));
      setDays(dl.days != null ? String(dl.days) : "");
    } finally {
      setBusy(false);
    }
  };

  const tone = actionKey === "postdue" ? "is-postdue" : actionKey === "remind" ? "is-remind" : "";
  const count = Math.max(entry.reminder_count, reminders.length);

  return (
    <section className={`gf-dl ${tone}`}>
      <div className="gf-dl-row">
        <span className="gf-dl-title">制作期限</span>
        <span className="gf-dl-flow">
          発送 <b className="gf-mono">{fmtMD(entry.shipped_at) || "—"}</b> → 期限 <b className="gf-mono">{fmtMD(dl.dueYmd)}</b>
        </span>
        <span className="gf-dl-days">
          （
          <input
            className="gf-dl-input gf-mono"
            type="number"
            min={1}
            max={365}
            inputMode="numeric"
            value={days}
            disabled={busy || !shipYmd}
            aria-label="制作期限の日数"
            onChange={(e) => setDays(e.target.value)}
            onBlur={() => void commit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          日）
        </span>
      </div>
      <div className="gf-dl-row is-sub">
        <span className={`gf-dl-left${dl.overdue ? " is-over" : actionKey === "remind" ? " is-remind" : ""}`}>
          {entry.post_count > 0 ? "投稿済" : dl.overdue ? `超過${-dl.daysLeft}日` : `残り${dl.daysLeft}日`}
        </span>
        <span className="gf-dl-rem">リマインド {count}回</span>
      </div>
      {reminders.length > 0 && (
        <div className="gf-dl-hist">
          リマインド履歴：{reminders.map((r, i) => `${fmtMD(r.created_at)}（${i + 1}回目）`).join("、")}
        </div>
      )}
    </section>
  );
}
